import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { 
  Workflow,
  TaskDefinition,
  TaskType,
  DateMeta,
  ResourceToDateFieldsMap,
  ValidResourceDateFields,
  Roles,
  DateMetaTypes,
  Periods,
} from '@models/workflow.model';
import { User } from '@models/user.model';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { CompanyService } from '@services/company.service';
import { Toaster } from '@services/toaster.service';
import { LocalStorage } from 'ngx-webstorage';
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import CONFIG from '@app/app.config';
import Swal from 'sweetalert2';

@Component({
  selector: 'ngx-task-creation',
  templateUrl: './task-creation-modal.component.html',
  styleUrls: ['./task-creation-modal.component.scss'],
})
export class TaskCreationModalComponent implements OnInit, OnDestroy {
  taskForm: FormGroup;
  notificationsForm: FormGroup;

  @Input() organizationId;
  @Input() maxIndex;
  @Input() index = 0;
  @Input() workflow: Workflow;
  @Input() task: TaskDefinition;

  taskTypes: TaskType[];
  selectedTaskType: TaskType;

  users: User[];
  selectedUser: User;

  @Input() tasks: TaskDefinition[];
  selectedTaskDependency: TaskDefinition;
  selectedTaskForApproval: TaskDefinition;

  selectedDateMetaType: string = 'specific';
  selectedIntendedForRole: string;

  validResourceDateFields: ResourceToDateFieldsMap[] = ValidResourceDateFields;
  selectedResource: ResourceToDateFieldsMap;

  roles: any[] = Roles;
  periods: any[] = Periods;
  dateMetaTypes: any[] = DateMetaTypes;

  loading: boolean = false;
  closeWithResult: boolean = false;

  selectedFormView: string = 'task';

  // taskDescription: string;

  constructor(
    private ngbActiveModal: NgbActiveModal,
    private fb: FormBuilder,
    private $company: CompanyService,
    private $toaster: Toaster,
  ) {
  }

  async ngOnInit() {
    this.loading = true;
    this.taskForm = this.fb.group({
      name: [null, [Validators.required]],
      description: [null, [Validators.required]],
      is_enabled: [true, [Validators.required]],
      allow_anyone_to_complete: [false, [Validators.required]],
      task_type_id: [null, [Validators.required]],
      default_responsible_id: [null, []],
      entity_name: [null, [Validators.required]],
      order: [this.index, [Validators.required]],
      depends_on_task: [null, []],
      affected_task: [null, []],
      intended_for_role: [this.roles[1].name, [Validators.required]],
      due_date_meta: this.fb.group({
        number: [null, []],
        period: [null, []],
        before_after: [null, []],
        resource: [null, []],
        resource_id: [null, []],
        resource_date_field: [null, []],
        specific_date: [null, []],
        date_meta_type: [this.selectedDateMetaType, [Validators.required]],
      }),
    });

    this.selectedIntendedForRole = this.roles[1].name;

    this.notificationsForm = this.fb.group({
      //
    });

    await this.$company.workflowTaskTypes().subscribe(taskTypes => {
      this.taskTypes = taskTypes;
      if (this.taskTypes.length && !this.task) {
        this.taskForm.patchValue({'task_type_id': this.taskTypes[0].id});
        this.selectTaskType({value: this.taskTypes[0].id});
      }
      if (this.taskTypes.length && this.task) {
        this.selectTaskType({value: this.task.task_type.id});
      }
    }, (error) => {
      this.$toaster.show('error', 'An error occurred', 'Please try again later');
      console.error(error);
    });

    await this.$company.users(this.organizationId).subscribe(users => {
      this.users = users;

      if (this.task && this.task.default_responsible_id) {
        this.selectUser({value: this.task.default_responsible_id});
      }
    }, (error) => {
      this.$toaster.show('error', 'An error occurred', 'Please try again later');
      console.error(error);
    });


    if (this.task && this.task.id) {
      this.taskForm.patchValue(this.task);
      this.taskForm.patchValue({'task_type_id': this.task.task_type.id});
      // this.taskDescription = this.task.description;
      this.selectedIntendedForRole = this.task.intended_for_role;
      if (this.task.affected_task) {
        this.selectTaskForApproval({value: this.task.affected_task});
      }
      if (this.task.due_date_meta.specific_date) {
        this.taskForm.patchValue({
          'due_date_meta': {
            'specific_date': this.task.due_date_meta.specific_date,
            date_meta_type: this.selectedDateMetaType,
          }
        });
      } else {
        this.selectedDateMetaType = 'relative';
        this.taskForm.patchValue({
          'due_date_meta': {
            number: this.task.due_date_meta.number,
            period: this.task.due_date_meta.period,
            before_after: this.task.due_date_meta.before_after,
            resource: this.task.due_date_meta.resource,
            resource_date_field: this.task.due_date_meta.resource_date_field,
            date_meta_type: this.selectedDateMetaType,
          },
        });
        this.selectedResource = this.validResourceDateFields.find(c => c.resource_name === this.task.due_date_meta.resource);
      }
    }
    this.loading = false;
  }

  save() {
    if (!this.taskForm.valid) {
      return;
    }
    let taskPayload = new TaskDefinition(this.taskForm.getRawValue());
    // taskPayload.description = this.taskDescription;
    let request;

    if (this.task && this.task.id) {
      request = this.$company.updateTaskDefinition(
        this.organizationId, this.workflow.id, this.task.id, taskPayload,
      );
    } else {
      request = this.$company.createTaskDefinition(
        this.organizationId, this.workflow.id, taskPayload,
      );
    }

    request.subscribe((result) => {
      if (this.task && this.task.id) {
        this.$toaster.show('success', 'Success', 'Task has been updated');
        this.ngbActiveModal.close(result);
        return;
      }

      this.$toaster.show('success', 'Success', 'Task has been added');
      Swal.fire({
        title: 'Continue editing?',
        text: `Your task has been added successfully. 
        Do you want to add files and notifications to this task or close the window?`,
        icon: 'question',
        cancelButtonText: 'Close window',
        showCancelButton: true,
        buttonsStyling: false,
        confirmButtonText: 'Continue editing',
        customClass: {
          confirmButton: 'btn btn-primary mr-2',
          cancelButton: 'btn btn-secondary ml-2',
        },
        confirmButtonColor: '#41b146',
      }).then((res) => {
        if (res.isConfirmed === true) {
          this.closeWithResult = true;
          this.getTask(result.id);
          return;
        }
        this.ngbActiveModal.close(result);
      });

    }, (e) => {
      const errMsg = !!e.error && !!e.error.detail ? e.error.detail : e.message;
      this.$toaster.show('error', 'An error occurred', 'Please try again later');
    });
  }

  getTask(taskId) {
    this.$company.workflowTaskDefinition(this.organizationId, this.workflow.id, taskId)
      .subscribe(res => {
        this.task = res;
      }, error => {
        console.error(error);
        this.$toaster.show('error', 'An error occurred', 'Please try again later');
      })
  }

  selectTaskType($event) {
    this.selectedTaskType = this.taskTypes.find(c => c.id === $event.value);
    if (this.selectedTaskType.interaction_type !== 'approve') {
      this.selectedTaskForApproval = null;
      this.taskForm.patchValue({'affected_task': null});
    }
  }

  selectIntendedForRole($event) {
    this.selectedIntendedForRole = $event.value;
    if (this.selectedUser && this.selectedIntendedForRole === 'suppliers') {
      this.taskForm.patchValue({'default_responsible_id': null});
      this.selectedUser = null;
    }
  }

  selectUser($event) {
    this.selectedUser = this.users.find(c => c.id === $event.value);
    if (this.selectedUser && this.selectedIntendedForRole === 'suppliers') {
      this.taskForm.patchValue({'intended_for_role': 'customers'});
      this.selectedIntendedForRole = null;
    }
  }

  selectTaskDependency($event) {
    this.selectedTaskDependency = this.tasks.find(c => c.id === $event.value);
  }

  selectTaskForApproval($event) {
    let selection = this.tasks.find(c => c.id === $event.value);
    if (!selection && this.selectedTaskType?.interaction_type === 'approve') {
      Swal.fire({
        title: 'Confirmation required',
        text: `Clearing this field will also clear the task type, are you sure?`,
        icon: 'question',
        showCancelButton: true,
        buttonsStyling: false,
        confirmButtonText: 'Confirm',
        customClass: {
          confirmButton: 'btn btn-primary mr-2',
          cancelButton: 'btn btn-secondary ml-2',
        },
        confirmButtonColor: '#41b146',
      }).then((res) => {
        if (res.isConfirmed === true) {
          this.taskForm.patchValue({'task_type_id': null});
          this.selectTaskType({value: null});
          this.selectedTaskForApproval = null;
        }
      });
    } else if (selection) {
      this.selectedTaskForApproval = selection;
      this.taskForm.patchValue({'depends_on_task': selection.id});
      this.selectTaskDependency({value: selection.id});
    }
  }

  selectDateMetaType($event) {
    this.selectedDateMetaType = this.dateMetaTypes.find(c => c.key === $event.value).key;

    if (this.selectedDateMetaType === 'specific') {
      if (this.task && this.task.id) {
        this.taskForm.patchValue({'due_date_meta': {'specific_date': this.task.due_date_meta.specific_date}});
      }

      this.taskForm.patchValue({
        'due_date_meta': {
          number: null,
          period: null,
          before_after: null,
          resource: null,
          resource_id: null,
          resource_date_field: null,
        },
      });
      this.selectedResource = null;
      
    } else {
      if (this.task && this.task.id && this.task.due_date_meta.number !== null) {
        this.taskForm.patchValue({
          'due_date_meta': {
            number: this.task.due_date_meta.number,
            period: this.task.due_date_meta.period,
            before_after: this.task.due_date_meta.before_after,
            resource: this.task.due_date_meta.resource,
            resource_id: this.task.due_date_meta?.resource_id || null,
            resource_date_field: this.task.due_date_meta.resource_date_field,
            specific_date: null,
          },
        });
        this.selectedResource = this.validResourceDateFields.find(c => c.resource_name === this.task.due_date_meta.resource);
      } else {
        this.taskForm.patchValue({
          'due_date_meta': {
            number: 1,
            period: this.periods[0].key,
            before_after: 'after',
            resource: this.validResourceDateFields[0].resource_name,
            resource_id: null,
            specific_date: null,
          },
        });
        this.selectResource({value: this.validResourceDateFields[0].resource_name});
      }
    }

  }

  selectResource($event) {
    this.selectedResource = this.validResourceDateFields.find(c => c.resource_name === $event.value);
    this.taskForm.patchValue({
      'due_date_meta': {resource_date_field: this.selectedResource.fields[0].field_name},
    });
  }

  saveNotifications() {
    //
  }

  closeModal() {
    if (this.closeWithResult) {
        this.ngbActiveModal.close(this.task.id);
        return;
    }
    this.ngbActiveModal.dismiss();
  }

  ngOnDestroy(): void {
  }

}
